// src/admin/pages/AdminProductAnalytics.js
import React, { useContext, useEffect, useMemo, useState } from "react";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { FaInfoCircle } from "react-icons/fa";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import {
  FaChartBar,
  FaChartLine,
  FaHeartbeat,
  FaCrown,
  FaBoxes,
  FaExclamationTriangle,
} from "react-icons/fa";
import { getProductsAnalytics } from "../../services/productsAnalytics.api";
import { useNavigate } from "react-router-dom";

const InfoTooltip = ({ text }) => (
  <div className="relative group">
    <FaInfoCircle className="text-gray-400 text-[13px] cursor-pointer" />

    <div className="
      absolute z-50 hidden group-hover:block
      bottom-full mb-2 left-1/2 -translate-x-1/2
      w-52 p-2 rounded-md
      text-xs text-white bg-gradient-to-t from-gray-900 to-gray-900
      shadow-lg
    ">
      {text}
    </div>
  </div>
);

function AdminProductAnalytics() {
  const { lang } = useContext(AdminLanguageContext);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");
  const perms = JSON.parse(localStorage.getItem("staffPermissions") || {});
  const isAdmin = adminUser.role === "admin";
  const canAccessAnalytics = isAdmin || perms.can_view_analytics;

  const [period, setPeriod] = useState(14);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);

  const formatMoney = (v) =>
  `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  const TEXT = {
    EN: {
      title: "Product Analytics",
      loading: "Loading product analytics...",
      denied: "You are not allowed to access Product Analytics.",
      sellThrough: "Sell-through rate",
      inventoryHealth: "Inventory health",
      topProducts: "Top products",
      unitsSold14d: "Units sold (14 days)",
      overview: "Overview",
      salesTrend: "Sales trend",
      topProductsTable: "Top-selling products",
      topSkusTable: "Top SKUs",
      lowStockTable: "Low stock attention",
      product: "Product",
      sku: "SKU",
      variant: "Variant",
      sold: "Sold",
      stock: "Stock",
      date: "Date",
      totalProducts: "Total products",
      activeProducts: "Active products",
      totalVariants: "Total variants",
      totalStock: "Total stock",
      lowStockVariants: "Low-stock variants",
      noData: "No data available.",
    },
    AR: {
      title: "تحليلات المنتجات",
      loading: "جاري تحميل تحليلات المنتجات...",
      denied: "غير مسموح لك بالدخول إلى تحليلات المنتجات.",
      sellThrough: "معدل البيع",
      inventoryHealth: "صحة المخزون",
      topProducts: "أفضل المنتجات",
      unitsSold14d: "الوحدات المباعة (14 يومًا)",
      overview: "نظرة عامة",
      salesTrend: "اتجاه المبيعات",
      topProductsTable: "المنتجات الأكثر مبيعًا",
      topSkusTable: "أفضل المتغيرات",
      lowStockTable: "المنتجات منخفضة المخزون",
      product: "المنتج",
      sku: "SKU",
      variant: "المتغير",
      sold: "المباع",
      stock: "المخزون",
      date: "التاريخ",
      totalProducts: "إجمالي المنتجات",
      activeProducts: "المنتجات النشطة",
      totalVariants: "إجمالي المتغيرات",
      totalStock: "إجمالي المخزون",
      lowStockVariants: "المتغيرات منخفضة المخزون",
      noData: "لا توجد بيانات.",
    },
  }[lang];

  useEffect(() => {
    if (!canAccessAnalytics) {
      setLoading(false);
      return;
    }

    setLoading(true);
    getProductsAnalytics(`?days=${period}`)
      .then((data) => setAnalytics(data))
      .catch((err) => console.error("Load product analytics error:", err))
      .finally(() => setLoading(false));
  }, [canAccessAnalytics, period]);

  const unitsSold14d = useMemo(() => {
    return (analytics?.sales_trend || []).reduce(
      (sum, item) => sum + Number(item.units_sold || 0),
      0
    );
  }, [analytics]);

  const maxTrend = useMemo(() => {
    const values = (analytics?.sales_trend || []).map((i) =>
      Number(i.units_sold || 0)
    );
    return Math.max(...values, 1);
  }, [analytics]);

  const avg = useMemo(() => {
  const arr = analytics?.sales_trend || [];
  if (!arr.length) return 0;

  return (
    arr.reduce((sum, i) => sum + Number(i.units_sold || 0), 0) /
    arr.length
  );
}, [analytics]);

  const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

  const MetricCard = ({ title, value, icon, tone = "gray", tooltip }) => {
    const tones = {
      blue: {
        iconBg: "bg-blue-50",
        iconText: "text-blue-600",
        bar: "bg-blue-500",
      },
      green: {
        iconBg: "bg-emerald-50",
        iconText: "text-emerald-600",
        bar: "bg-emerald-500",
      },
      amber: {
        iconBg: "bg-amber-50",
        iconText: "text-amber-600",
        bar: "bg-amber-500",
      },
      gray: {
        iconBg: "bg-gray-100",
        iconText: "text-gray-600",
        bar: "bg-gray-500",
      },
    };

    return (
      <div className="bg-white border border-[#e3e3e3] rounded-[14px] p-5 shadow-[0_12px_32px_rgba(0,0,0,0.08)]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[13px] font-medium text-gray-500 flex items-center gap-1">
                {title}
                {tooltip && <InfoTooltip text={tooltip} />}
                </div>
            <div className="text-[28px] leading-none font-semibold text-gray-900 mt-2">
              {value}
            </div>
          </div>

          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[tone].iconBg}`}
          >
            <span className={tones[tone].iconText}>{icon}</span>
          </div>
        </div>

        <div className="mt-4 h-2 w-full rounded-full bg-gray-100 overflow-hidden">
          <div className={`h-full rounded-full ${tones[tone].bar}`} style={{ width: "100%" }} />
        </div>
      </div>
    );
  };

  const SectionCard = ({ title, children }) => (
    <div className="bg-white border border-[#e3e3e3] rounded-[14px] p-5 shadow-[0_12px_32px_rgba(0,0,0,0.08)]">
      <h2 className="text-sm font-semibold text-gray-900 mb-4">{title}</h2>
      {children}
    </div>
  );

  const ProductAnalyticsSkeleton = () => (
  <div className="space-y-5 animate-pulse">
    {/* TOP METRICS */}
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="bg-white border border-[#e3e3e3] rounded-[14px] p-5 shadow-[0_12px_32px_rgba(0,0,0,0.08)]"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-3">
              <div className="h-4 w-28 bg-gray-200 rounded" />
              <div className="h-7 w-20 bg-gray-200 rounded" />
            </div>

            <div className="w-10 h-10 rounded-xl bg-gray-200" />
          </div>

          <div className="mt-4 h-2 w-full rounded-full bg-gray-200" />
        </div>
      ))}
    </div>

    {/* OVERVIEW */}
    <div className="bg-white border border-[#e3e3e3] rounded-[14px] p-5 shadow-[0_12px_32px_rgba(0,0,0,0.08)]">
      <div className="h-4 w-28 bg-gray-200 rounded mb-4" />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="rounded-xl border border-gray-200 bg-[#fafafa] px-4 py-4"
          >
            <div className="h-3 w-24 bg-gray-200 rounded" />
            <div className="mt-3 h-7 w-12 bg-gray-200 rounded" />
          </div>
        ))}
      </div>
    </div>

    {/* CHART */}
    <div className="bg-white border border-[#e3e3e3] rounded-[14px] p-5 shadow-[0_12px_32px_rgba(0,0,0,0.08)]">
      <div className="h-4 w-32 bg-gray-200 rounded mb-5" />

      <div className="flex items-end gap-2 h-52">
        {Array.from({ length: 14 }).map((_, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-2">
            <div className="h-3 w-5 bg-gray-200 rounded" />
            <div className="w-full flex items-end h-40">
              <div
                className="w-full rounded-t-md bg-gray-200"
                style={{ height: `${30 + ((i % 5) * 12)}%` }}
              />
            </div>
            <div className="h-3 w-8 bg-gray-200 rounded" />
          </div>
        ))}
      </div>
    </div>

    {/* TABLES */}
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
      {Array.from({ length: 2 }).map((_, cardIndex) => (
        <div
          key={cardIndex}
          className="bg-white border border-[#e3e3e3] rounded-[14px] p-5 shadow-[0_12px_32px_rgba(0,0,0,0.08)]"
        >
          <div className="h-4 w-36 bg-gray-200 rounded mb-4" />

          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, rowIndex) => (
              <div
                key={rowIndex}
                className="flex items-center justify-between border-b border-gray-100 pb-3"
              >
                <div className="space-y-2">
                  <div className="h-4 w-36 bg-gray-200 rounded" />
                  <div className="h-3 w-20 bg-gray-200 rounded" />
                </div>

                <div className="h-4 w-10 bg-gray-200 rounded" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ProductAnalyticsEmptyState = () => (
  <div className="bg-white border border-[#e3e3e3] rounded-[14px] p-10 text-center shadow-[0_12px_32px_rgba(0,0,0,0.08)]">
    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
      <FaChartBar className="text-gray-400 text-xl" />
    </div>

    <h3 className="text-sm font-semibold text-gray-800">
      {TEXT.noData}
    </h3>

    <p className="text-sm text-gray-500 mt-1">
      {lang === "AR"
        ? "ستظهر تحليلات المنتجات بعد توفر بيانات مبيعات أو مخزون."
        : "Product analytics will appear once sales or inventory data is available."}
    </p>
  </div>
);

  if (!canAccessAnalytics) {
    return (
      <div className={`flex bg-[#f1f1f1] min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
        <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />
        <div className={`flex-1 ${lang === "EN" ? "lg:ml-64" : ""} w-full transition-all duration-300`}>
          <AdminHeader setIsOpen={setIsOpen} />
          <main className="p-4 sm:p-6">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <p className="text-red-700 font-semibold text-lg">{TEXT.denied}</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex bg-[#f1f1f1] min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div className={`flex-1 ${lang === "EN" ? "lg:ml-64" : ""} w-full transition-all duration-300`}>
        <AdminHeader setIsOpen={setIsOpen} />

        <main className="p-4 sm:p-6 animate-fadeSlide space-y-5">
          {/* HEADER */}
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-6 h-6">
              <FaChartBar className="text-gray-900 text-[18px]" />
            </div>
            <h1 className="text-[20px] font-semibold text-gray-900">{TEXT.title}</h1>
          </div>

          {loading ? (
  <ProductAnalyticsSkeleton />
) : !analytics ? (
  <ProductAnalyticsEmptyState />
) : (
            <>
              {/* TOP METRICS */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
                <MetricCard
                title={TEXT.sellThrough}
                value={formatPercent(analytics?.sell_through)}
                icon={<FaChartLine />}
                tone="blue"
                tooltip="How much of your inventory is sold vs total available stock"
                />

                <MetricCard
                title={TEXT.inventoryHealth}
                value={formatPercent(analytics?.inventory_health)}
                icon={<FaHeartbeat />}
                tone="green"
                tooltip="Percentage of variants with healthy stock (>3 units)"
                />

                <MetricCard
                title={TEXT.topProducts}
                value={formatPercent(analytics?.top_products_percentage)}
                icon={<FaCrown />}
                tone="amber"
                tooltip="Top 20% of products contributing to sales"
                />

                <MetricCard
                title={TEXT.unitsSold14d}
                value={unitsSold14d}
                icon={<FaBoxes />}
                tone="gray"
                tooltip="Total units sold in selected period"
                />
                <MetricCard
                title={lang === "EN" ? "Revenue" : "الإيرادات"}
                value={formatMoney(analytics?.revenue)}
                icon={<FaChartLine />}
                tone="blue"
                tooltip="Estimated revenue from sold units (base price + variant extra price)"
                />
              </div>

              {/* OVERVIEW */}
              <SectionCard title={TEXT.overview}>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
                  {[
                    { label: TEXT.totalProducts, value: analytics?.overview?.total_products ?? 0 },
                    { label: TEXT.activeProducts, value: analytics?.overview?.active_products ?? 0 },
                    { label: TEXT.totalVariants, value: analytics?.overview?.total_variants ?? 0 },
                    { label: TEXT.totalStock, value: analytics?.overview?.total_stock ?? 0 },
                    { label: TEXT.lowStockVariants, value: analytics?.overview?.low_stock_variants ?? 0 },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="rounded-xl border border-gray-200 bg-[#fafafa] px-4 py-4"
                    >
                      <div className="text-xs text-gray-500">{item.label}</div>
                      <div className="mt-2 text-2xl font-semibold text-gray-900">
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>

                <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  Period
                </div>

                <div className="flex bg-gray-100 p-1 rounded-lg">
                  {[7, 14, 30].map((d) => (
                    <button
                      key={d}
                      onClick={() => setPeriod(d)}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                        period === d
                          ? "bg-white shadow text-gray-900"
                          : "text-gray-500 hover:text-gray-900"
                      }`}
                    >
                      {d}d
                    </button>
                  ))}
                </div>
              </div>

              {/* SALES TREND */}
              <SectionCard
                title={
                    <div className="flex items-center gap-1">
                    {TEXT.salesTrend}
                    <InfoTooltip text="Daily units sold trend for selected period" />
                    </div>
                }
                >
                {!analytics?.sales_trend?.length ? (
                  <div className="text-sm text-gray-500">{TEXT.noData}</div>
                ) : (
                  <div className="space-y-3">
                    <div className="relative">
                      {/* ✅ AVG LINE */}
                      <div
                        className="absolute left-0 right-0 border-t border-dashed border-gray-300 z-10"
                        style={{ bottom: `${(avg / maxTrend) * 100}%` }}
                      />

                      <div className="flex items-end gap-2 h-52">
                      {analytics.sales_trend.map((item) => {
                        const height = `${Math.max(
                          12,
                          (Number(item.units_sold || 0) / maxTrend) * 100
                        )}%`;

                        return (
                          <div key={item.date} className="flex-1 flex flex-col items-center gap-2">

                            {/* ✅ VALUE LABEL */}
                              <div className="text-[10px] text-gray-900 font-medium">
                              {item.units_sold}
                            </div>

                            <div className="w-full flex items-end h-40">
                              <div
                                className="w-full rounded-t-md bg-gray-900 transition-all duration-500 hover:bg-gray-900"
                                style={{ height }}
                                title={`${item.date} → ${item.units_sold} units`}
                              />
                            </div>
                            <div className="text-[10px] text-gray-500 whitespace-nowrap">
                              {item.date.slice(5)}
                            </div>
                          </div>
                        );
                      })}
                    </div> 
                    </div>
                  </div>
                )}
              </SectionCard>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                {/* TOP PRODUCTS */}
                <SectionCard title={TEXT.topProductsTable}>
                  {!analytics?.top_products?.length ? (
                    <div className="text-sm text-gray-500">{TEXT.noData}</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="text-left text-gray-500 border-b">
                          <tr>
                            <th className="py-2">{TEXT.product}</th>
                            <th className="py-2 text-center">{TEXT.sold}</th>
                            <th className="py-2 text-center">{TEXT.stock}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics.top_products.map((item) => (
                            <tr
                                key={item.id}
                                className="border-b last:border-b-0 cursor-pointer hover:bg-gray-50"
                                onClick={() => navigate(`/admin/products/edit/${item.id}`)}
                                >
                              <td className="py-3">
                                <div className="font-medium text-gray-900">
                                  {lang === "AR" ? item.name_ar || item.name_en : item.name_en}
                                </div>
                                <div className="text-xs text-gray-500">{item.public_id}</div>
                              </td>
                              <td className="py-3 text-center font-semibold">{item.units_sold}</td>
                              <td className="py-3 text-center">{item.stock_left}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </SectionCard>

                {/* TOP SKUS */}
                <SectionCard title={TEXT.topSkusTable}>
                  {!analytics?.top_skus?.length ? (
                    <div className="text-sm text-gray-500">{TEXT.noData}</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="text-left text-gray-500 border-b">
                          <tr>
                            <th className="py-2">{TEXT.variant}</th>
                            <th className="py-2">{TEXT.sku}</th>
                            <th className="py-2 text-center">{TEXT.sold}</th>
                            <th className="py-2 text-center">{TEXT.stock}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics.top_skus.map((item) => (
                            <tr key={item.variant_id} className="border-b last:border-b-0">
                              <td className="py-3">
                                <div className="font-medium text-gray-900">
                                  {lang === "AR" ? item.name_ar || item.name_en : item.name_en}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {item.color_name} / {item.size}
                                </div>
                              </td>
                              <td className="py-3 text-gray-600">{item.sku}</td>
                              <td className="py-3 text-center font-semibold">{item.units_sold}</td>
                              <td className="py-3 text-center">{item.stock_qty}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </SectionCard>
              </div>

              {/* LOW STOCK */}
              <SectionCard
                  title={
                    <div className="flex items-center gap-1">
                      {TEXT.lowStockTable}
                      <InfoTooltip text="Products predicted to run out within 30 days based on recent sales velocity" />
                    </div>
                  }
                >
                {!analytics?.predicted_low_stock?.length ? (
                  <div className="text-sm text-gray-500">{TEXT.noData}</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-left text-gray-500 border-b">
                        <tr>
                          <th className="py-2">{TEXT.product}</th>
                          <th className="py-2">{TEXT.variant}</th>
                          <th className="py-2">{TEXT.sku}</th>
                          <th className="py-2 text-center">{TEXT.stock}</th>
                          <th className="py-2 text-center">Days left</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.predicted_low_stock?.map((item) => (
                          <tr
                                key={item.variant_id}
                                className="border-b last:border-b-0 cursor-pointer hover:bg-gray-50"
                                onClick={() => navigate(`/admin/products/edit/${item.product_id}`)}
                                >
                            <td className="py-3">
                              <div className="font-medium text-gray-900">
                                {lang === "AR" ? item.name_ar || item.name_en : item.name_en}
                              </div>
                              <div className="text-xs text-gray-500">{item.public_id}</div>
                            </td>
                            <td className="py-3 text-gray-600">
                              {item.color_name} / {item.size}
                            </td>
                            <td className="py-3 text-gray-600">{item.sku}</td>
                            <td className="py-3 text-center">
                            <span
                              className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-md text-xs font-medium ${
                                Number(item.stock_qty) === 0
                                  ? "bg-red-50 text-red-700"
                                  : "bg-amber-50 text-amber-700"
                              }`}
                            >
                              <FaExclamationTriangle className="text-[11px]" />
                              {item.stock_qty}
                            </span>
                          </td>

                          <td className="py-3 text-center text-xs font-medium text-gray-600">
                            {item.days_left ?? "—"}d
                          </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </SectionCard>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default AdminProductAnalytics;